/**
 * Hook for fetching and managing tooltip words via Cloud Function
 * Populates the global tooltip registry on mount
 */

import { useState, useEffect, useCallback } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../Models/firebaseConfig";
import { setTooltipWords, TooltipWord } from "../util/wordTooltipProcessor";

interface FirestoreTooltipWord {
  id?: string;
  ID?: string;
  Phrase: string;
  Title?: string;
  Explanation: string;
  Synonyms?: string[];
  AppIDs?: string[];
  TheoryIDs?: string[];
  GeneratedByAI?: boolean;
}

interface GetTooltipWordsResponse {
  success: boolean;
  tooltipWords: FirestoreTooltipWord[];
  count: number;
}

interface UseTooltipWordsDataResult {
  tooltipWords: TooltipWord[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Converts Firestore tooltip word document to frontend TooltipWord format
 */
function convertFirestoreToTooltipWord(
  doc: FirestoreTooltipWord
): TooltipWord {
  return {
    id: doc.id || doc.ID,
    phrase: doc.Phrase,
    title: doc.Title,
    explanation: doc.Explanation,
    synonyms: doc.Synonyms,
  };
}

/**
 * Hook that fetches tooltip words for an app via Cloud Function
 * and populates the global registry
 *
 * @param appId - The app/education ID to fetch tooltip words for
 * @returns Object with tooltipWords array, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { tooltipWords, isLoading, error } = useTooltipWordsData(currentAppId);
 * ```
 */
export function useTooltipWordsData(appId: string | null): UseTooltipWordsDataResult {
  const [tooltipWords, setLocalTooltipWords] = useState<TooltipWord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTooltipWords = useCallback(async () => {
    if (!appId) {
      setLocalTooltipWords([]);
      setTooltipWords([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getTooltipWordsForApp = httpsCallable<
        { appId: string },
        GetTooltipWordsResponse
      >(functions, "getTooltipWordsForApp");

      const result = await getTooltipWordsForApp({ appId });

      if (result.data.success && result.data.tooltipWords) {
        const words: TooltipWord[] = result.data.tooltipWords.map(
          convertFirestoreToTooltipWord
        );

        // Update local state
        setLocalTooltipWords(words);

        // Update global registry for processHTMLWithTooltips
        setTooltipWords(words);
      } else {
        setLocalTooltipWords([]);
        setTooltipWords([]);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch tooltip words";
      console.error("[useTooltipWordsData] Error:", errorMessage);
      setError(errorMessage);
      
      // Clear tooltip words on error to avoid stale data
      setLocalTooltipWords([]);
      setTooltipWords([]);
    } finally {
      setIsLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    fetchTooltipWords();
  }, [fetchTooltipWords]);

  return {
    tooltipWords,
    isLoading,
    error,
    refetch: fetchTooltipWords,
  };
}

export default useTooltipWordsData;
